import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';

export default function TrainingResgistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch registrations on mount
  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    try {
      const response = await fetch('https://elexdondigitalacademy.com/api/get_registrations.php');
      const data = await response.json();
      if (data.success) {
        setRegistrations(data.data);
      } else {
        Swal.fire('Error', data.error || 'Failed to load records.', 'error');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      Swal.fire('Error', 'Network error while fetching registrations.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle status change via dropdown
  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await fetch('https://elexdondigitalacademy.com/api/update_registration_status.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status: newStatus })
      });
      const data = await response.json();

      if (data.success) {
        // Update local state instantly
        setRegistrations(prev =>
          prev.map(reg => (reg.id === id ? { ...reg, payment_status: newStatus } : reg))
        );
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Status updated successfully',
          showConfirmButton: false,
          timer: 2000
        });
      } else {
        Swal.fire('Error', data.error || 'Could not update status.', 'error');
      }
    } catch (error) {
      console.error('Update error:', error);
      Swal.fire('Error', 'Network error during status update.', 'error');
    }
  };

  // Filter records based on name or email search query
  const filteredRegistrations = registrations.filter(reg =>
    reg.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    reg.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <Container><p>Loading registrations...</p></Container>;
  }

  return (
    <Container>
      <HeaderSection>
        <h2>Training Registrations Management</h2>
        <SearchInput
          type="text"
          placeholder="Search by full name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </HeaderSection>

      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Full Name</th>
              <th>Email & Phone</th>
              <th>Course / Mode</th>
              <th>Price</th>
              <th>Payment Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredRegistrations.length > 0 ? (
              filteredRegistrations.map((reg) => (
                <tr key={reg.id}>
                  <td>{new Date(reg.created_at).toLocaleDateString()}</td>
                  <td><strong>{reg.full_name}</strong></td>
                  <td>
                    <div>{reg.email}</div>
                    <small style={{ color: '#64748b' }}>{reg.phone}</small>
                  </td>
                  <td>
                    <div>{reg.course_title}</div>
                    <Badge>{reg.course_mode}</Badge>
                  </td>
                  <td>₦{Number(reg.price).toLocaleString()}</td>
                  <td>
                    <StatusSelect
                      value={reg.payment_status}
                      status={reg.payment_status}
                      onChange={(e) => handleStatusChange(reg.id, e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                    </StatusSelect>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '30px' }}>
                  No registration records found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableWrapper>
    </Container>
  );
}

// --- Styled Components ---
const Container = styled.div`
  max-width: 1200px;
  margin: 40px auto;
  padding: 0 20px;
  font-family: inherit;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  flex-wrap: wrap;
  gap: 15px;

  h2 {
    color: #0f172a;
    font-size: 1.8rem;
    margin: 0;
  }
`;

const SearchInput = styled.input`
  padding: 10px 15px;
  width: 300px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.95rem;
  outline: none;
  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const TableWrapper = styled.div`
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  border: 1px solid #e2e8f0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.95rem;

  th, td {
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
  }

  th {
    background-color: #f8fafc;
    color: #475569;
    font-weight: 600;
  }

  tr:hover {
    background-color: #f8fafc;
  }
`;

const Badge = styled.span`
  display: inline-block;
  background: #e0f2fe;
  color: #0369a1;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-top: 4px;
`;

const StatusSelect = styled.select`
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  border: 1px solid #cbd5e1;
  background-color: ${({ status }) => (status === 'paid' ? '#dcfce7' : '#fef9c3')};
  color: ${({ status }) => (status === 'paid' ? '#166534' : '#854d0e')};

  &:focus {
    outline: none;
  }
`;